import EmptyState from '@/components/EmptyState';
import { featureFlags } from '@/featureFlags';
import { useT } from '@/i18n';
import { formatPhone } from '@/utils/phone';
import {
  useAddCustomerPhone,
  useCustomer,
  useCustomerCoupons,
  useRemoveCustomerPhone,
  useSetPrimaryPhone,
  useUpdateCustomer,
} from '@/features/customers/hooks/useCustomer';
import { CouponHistoryList } from '@/features/promotions/components/CouponHistoryList';
import { CustomerUpdateSchema } from '@restaurant/api-contracts';
import {
  Badge,
  Block,
  BlockTitle,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useState } from 'react';
import { LoyaltyProfileSection } from '@/features/loyalty/components/LoyaltyProfileSection';

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

// Rule 4 - the form schema IS the contract's body schema, reused directly.
const updateSchema = CustomerUpdateSchema;

function fmtDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString();
}

type ProfileField = 'firstName' | 'lastName' | 'email' | 'notes';

export default function CustomerDetailPage({
  f7route,
}: {
  f7route: { params: { customerId: string } };
}) {
  const customerId = f7route.params.customerId;
  const t = useT();
  const td = t.customers.detail;
  const { data: customer, isLoading, isError } = useCustomer(customerId);

  const { data: coupons } = useCustomerCoupons(customerId);

  const update = useUpdateCustomer(customerId);
  const addPhone = useAddCustomerPhone(customerId);
  const setPrimary = useSetPrimaryPhone(customerId);
  const removePhone = useRemoveCustomerPhone(customerId);

  // Core profile is edited INLINE on this page, save-on-blur (Rule 11) - not
  // behind a tap/sheet. Seeded from server data, re-synced when it changes.
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<ProfileField, string>>>({});
  // Customer is a *record* (not a settings screen): read-only by default,
  // an Edit button flips fields editable - the iOS Contacts pattern.
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setForm({
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email ?? '',
      notes: customer.notes ?? '',
    });
    setErrors({});
  }, [customer]);

  const setField = (field: ProfileField, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const commit = (field: ProfileField) => {
    if (!customer) return;
    const raw = form[field].trim();
    const current = customer[field] ?? '';
    if (raw === current) {
      setErrors((e) => ({ ...e, [field]: undefined }));
      return;
    }
    // Empty optional → omit (the contract has no nullable clear); names that
    // go empty fail the schema's min(1) and surface inline below.
    const patch: Record<string, string> = {};
    if (raw) patch[field] = raw;
    else if (field === 'firstName' || field === 'lastName') patch[field] = raw;

    const parsed = updateSchema.safeParse(patch);
    if (!parsed.success) {
      const msg = parsed.error.issues.find((i) => i.path[0] === field)?.message;
      setErrors((e) => ({ ...e, [field]: msg ?? td.invalidValue }));
      return;
    }
    if (!raw && !(field === 'firstName' || field === 'lastName')) {
      // blank optional + nothing to send → leave as-is
      setErrors((e) => ({ ...e, [field]: undefined }));
      return;
    }
    setErrors((e) => ({ ...e, [field]: undefined }));
    update.mutate(parsed.data, { onSuccess: () => toast(t.saved) });
  };

  const promptAddPhone = () => {
    f7.dialog.prompt(td.addPhoneDialog, td.addPhoneButton, (value) => {
      const v = value.trim();
      if (!v) return;
      addPhone.mutate({ phone: v }, { onSuccess: () => toast(td.phoneAdded) });
    });
  };

  const confirmRemovePhone = (phoneId: string, label: string) => {
    f7.dialog.confirm(td.removePhoneConfirm(label), td.removePhoneDialog, () => {
      removePhone.mutate(phoneId, { onSuccess: () => toast(td.phoneRemoved) });
    });
  };

  // Operator-facing "Delete customer". Maps to the `isActive=false` soft-delete
  // (recoverable at the DB level by support), but presented as a clean, final
  // removal - operators understand "delete", not an active/inactive toggle.
  // After deleting, leave the now-hidden record and go back to the list.
  const deleteCustomer = () => {
    if (!customer) return;
    const who = `${customer.firstName} ${customer.lastName}`.trim() || td.deleteCustomerFallback;
    f7.dialog.confirm(td.deleteCustomerConfirm(who), td.deleteCustomerTitle, () =>
      update.mutate(
        { isActive: false },
        {
          onSuccess: () => {
            toast(td.customerDeleted);
            f7.views.current.router.back();
          },
        },
      ),
    );
  };

  return (
    <Page name="customer-detail">
      <Navbar title={td.title} backLink={t.back}>
        {customer && (
          <NavRight>
            <F7Link onClick={() => setEditing((v) => !v)}>{editing ? td.done : t.edit}</F7Link>
          </NavRight>
        )}
      </Navbar>

      {isLoading && (
        <Block style={{ textAlign: 'center', marginTop: 40 }}>
          <Preloader />
        </Block>
      )}

      {!isLoading && (isError || !customer) && (
        <Block>
          <EmptyState
            icon="exclamationmark_triangle"
            title={td.loadingError}
            description={t.customers.loadingErrorDescription}
          />
        </Block>
      )}

      {!isLoading && !isError && customer && (
        <>
          <BlockTitle>{td.profile}</BlockTitle>
          <List strongIos dividersIos insetIos>
            {editing ? (
              <ListInput
                type="text"
                label={td.firstName}
                value={form.firstName}
                onInput={(e) => setField('firstName', (e.target as HTMLInputElement).value)}
                onBlur={() => commit('firstName')}
                errorMessage={errors.firstName}
                errorMessageForce={!!errors.firstName}
              />
            ) : (
              <ListItem
                title={td.name}
                after={`${customer.firstName} ${customer.lastName}`.trim()}
              />
            )}
            {editing && (
              <ListInput
                type="text"
                label={td.lastName}
                value={form.lastName}
                onInput={(e) => setField('lastName', (e.target as HTMLInputElement).value)}
                onBlur={() => commit('lastName')}
                errorMessage={errors.lastName}
                errorMessageForce={!!errors.lastName}
              />
            )}
            {editing ? (
              <ListInput
                type="email"
                label={td.emailLabel}
                placeholder={td.emailPlaceholder}
                value={form.email}
                onInput={(e) => setField('email', (e.target as HTMLInputElement).value)}
                onBlur={() => commit('email')}
                errorMessage={errors.email}
                errorMessageForce={!!errors.email}
              />
            ) : (
              <ListItem
                className="item-after-wrap"
                title={td.emailLabel}
                after={customer.email ?? '-'}
              />
            )}
            <ListItem title={td.dateOfBirth} after={customer.dateOfBirth ?? '-'} />
          </List>

          <BlockTitle>{td.phoneNumbers}</BlockTitle>
          <List strongIos dividersIos insetIos>
            {customer.phoneNumbers
              .filter((p) => p.isActive)
              .map((p) => (
                <ListItem
                  key={p.id}
                  swipeout={editing}
                  title={formatPhone(p.phone)}
                  footer={p.label ?? undefined}
                >
                  {p.isPrimary && (
                    <Badge slot="after" color="green">
                      {td.primary}
                    </Badge>
                  )}
                  {editing && (
                    <div slot="swipeout-actions-right">
                      {!p.isPrimary && (
                        <a
                          // No href: F7 routes a clicked link by its href, and a
                          // bare "#" reaches document.querySelectorAll("#") ->
                          // "'#' is not a valid selector". The swipeout-close
                          // class is what closes the row, not the href.
                          role="button"
                          className="swipeout-close color-blue"
                          onClick={() =>
                            setPrimary.mutate(p.id, {
                              onSuccess: () => toast('Set as primary'),
                            })
                          }
                        >
                          {td.primary}
                        </a>
                      )}
                      <a
                        role="button"
                        className="swipeout-close color-red"
                        onClick={() => confirmRemovePhone(p.id, formatPhone(p.phone))}
                      >
                        {td.removeAction}
                      </a>
                    </div>
                  )}
                </ListItem>
              ))}
            {customer.phoneNumbers.filter((p) => p.isActive).length === 0 && !editing && (
              <ListItem title={td.noPhoneNumbers} />
            )}
            {editing && (
              <ListItem title={td.addPhoneButton} link="#" onClick={promptAddPhone}>
                <i slot="media" className="f7-icons" style={{ color: '#34c759', fontSize: 24 }}>
                  plus_circle_fill
                </i>
              </ListItem>
            )}
          </List>

          {/* iOS Contacts pattern: empty optional fields are hidden when
              viewing, and only surface in edit mode so a note can be added. */}
          {(editing || customer.notes) && (
            <>
              <BlockTitle>{td.notes}</BlockTitle>
              <List strongIos dividersIos insetIos>
                {editing ? (
                  <ListInput
                    type="textarea"
                    placeholder={td.notesPlaceholder}
                    value={form.notes}
                    onInput={(e) => setField('notes', (e.target as HTMLTextAreaElement).value)}
                    onBlur={() => commit('notes')}
                    errorMessage={errors.notes}
                    errorMessageForce={!!errors.notes}
                    resizable
                  />
                ) : (
                  <ListItem>
                    <div slot="inner" style={{ whiteSpace: 'normal', padding: '8px 0' }}>
                      {customer.notes}
                    </div>
                  </ListItem>
                )}
              </List>
            </>
          )}

          <BlockTitle>{td.activity}</BlockTitle>
          <List strongIos dividersIos insetIos>
            <ListItem title={td.totalVisits} after={String(customer.totalVisits)} />
            <ListItem title={td.lastVisit} after={fmtDate(customer.lastVisitAt)} />
          </List>

          {/* Private coupons this customer has been granted, across promotions.
              Only surfaced when at least one was ever issued - a customer who
              never got a personal coupon shows no section at all (not an empty
              one). Loading/error stay hidden for the same reason. */}
          {coupons && coupons.length > 0 && (
            <CouponHistoryList
              title={t.promotions.customerCoupons}
              emptyText={t.promotions.noCustomerCoupons}
              items={coupons}
              primary="promotion"
            />
          )}

          {/* Destructive entity action, iOS-native red row, only in edit mode
              (Contacts "Delete Contact"). */}
          {editing && (
            <List strongIos dividersIos insetIos>
              <ListItem
                title={td.deleteCustomer}
                link="#"
                onClick={deleteCustomer}
                style={{ color: '#ff3b30' }}
              >
                <i slot="media" className="f7-icons" style={{ color: '#ff3b30', fontSize: 24 }}>
                  trash
                </i>
              </ListItem>
            </List>
          )}

          {featureFlags.loyalty && <LoyaltyProfileSection customerId={customerId} />}
        </>
      )}
    </Page>
  );
}
